import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { openRequestAccess } from "@/components/landing/RequestAccessDialog";

/**
 * Fail-closed holding page for public signup and legacy request-access routes.
 *
 * Keeping the requested pathname mounted gives crawlers a truthful noindex
 * response and avoids prerendering homepage content under a gated URL. The
 * visitor can deliberately open the controlled request form; no account is
 * created and no access decision is disclosed on this page.
 */
const RequestAccessRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Request limited pilot access | Myelina";

    const existingRobots = document.head.querySelector<HTMLMetaElement>(
      'meta[name="robots"]',
    );
    const robots = existingRobots ?? document.createElement("meta");
    const previousRobots = existingRobots?.getAttribute("content") ?? null;

    if (!existingRobots) {
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, follow");

    return () => {
      document.title = previousTitle;
      if (existingRobots) {
        if (previousRobots === null) {
          existingRobots.removeAttribute("content");
        } else {
          existingRobots.setAttribute("content", previousRobots);
        }
      } else {
        robots.remove();
      }
    };
  }, []);

  const requestSource = location.pathname === "/signup"
    ? "signup-gate"
    : "legacy-request-access";

  return (
    <main className="min-h-screen bg-background px-6 py-20 text-foreground">
      <section
        aria-labelledby="access-hold-heading"
        className="mx-auto flex max-w-2xl flex-col items-start gap-6 rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-12"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
          Limited pilot
        </p>
        <div className="space-y-3">
          <h1 id="access-hold-heading" className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Public account creation is paused.
          </h1>
          <p className="max-w-prose text-base leading-7 text-foreground/80">
            Myelina is operating as a limited pilot while production, privacy,
            and security controls are verified. You may request consideration
            for access, but submitting a request does not create an account or
            guarantee admission.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            type="button"
            onClick={() => openRequestAccess(requestSource)}
          >
            Request limited pilot access
          </Button>
          <Button asChild variant="outline">
            <Link to="/">Return to the public site</Link>
          </Button>
        </div>
      </section>
    </main>
  );
};

export default RequestAccessRedirect;
